(ns dre.modals.search.export.events
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.fx :as-alias dre.fx]
   [dre.modals.events :as-alias modals]
   [dre.network :as-alias network]
   [dre.shared.interface.search.pills :as pills]
   [dre.shared.interface.search.util :refer [normalized-blunt-new]]
   [dre.utils :refer [stringify-keys-with-namespaces]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path
  [:modals :search :export])

(def docx-mime
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document")

(def default-limit
  100)

(def max-limit
  10000)

(def max-references-limit
  5000)

(def download-options
  [{:id :selected :text "Selected"}
   {:id :all :text "All"}])

(def file-format-options
  [{:id :bib :text "BibTeX"}
   {:id :nbib :text "NBIB"}
   {:id :docx :text "Microsoft Word"}
   {:id :csv :text "CSV"}])

(def citation-format-options
  [{:id :apa :text "APA"}
   {:id :ama :text "AMA"}
   {:id :vancouver :text "Vancouver"}])

(defn ids->blunt
  [ids]
  (let [groups (group-by :source ids)
        pills
        (pills/or-pill
         (map
          (fn
            [[source ids]]
            (pills/and-pill
             [(pills/source-pill source)
              (pills/uris-pill (map
                                :uri
                                (distinct ids)))]))
          groups))
        blunt (normalized-blunt-new {:query/pills pills})]
    blunt))

;; events

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [opts]]
  (let [new-db (merge
                opts
                {:loading? true
                 :download-type (if (seq (:selected-articles opts))
                                  :selected
                                  :all)
                 :format (:id (first file-format-options))
                 :citation-format (:id (first citation-format-options))
                 :relations? false
                 :abstracts? false
                 :filename (str "citations." (name (:id (first file-format-options))))
                 :exclude-docsearch-link? false})
        new-db (update new-db :limit (fn [v] (min v default-limit)))]
   {:db new-db
    ::network/request [{:endpoint :app/settings}
                       {:on-success [::init-success]
                        :on-failure [::init-failure]}]})))

(rf/reg-event-fx
 ::init-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  {:db (let [articles-export (get (:body response) :articles-export)
             articles-export (merge db articles-export)]
         (-> db
             (assoc :loading? false)
             (assoc :format (keyword (get articles-export :format)))
             (assoc :filename (str "citations." (get articles-export :format)))
             (assoc :citation-format (keyword (get articles-export :citation-format)))
             (assoc :relations? (get articles-export :relations?))
             (assoc :abstracts? (get articles-export :abstracts?))
             (assoc :exclude-docsearch-link? (get articles-export :exclude-docsearch-link?))))}))

(rf/reg-event-fx
 ::init-failure
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [_]]
  {:db (assoc db :loading? false)}))


(rf/reg-event-fx
 ::update-data
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [path value]]
  {:db (assoc-in db path value)}))

(rf/reg-event-fx
 ::export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  (let [selection? (= :selected (:download-type db))
        blunt
        (cond
          (:single-article? db)
          (let [article (:selected-article db)
                ids (cons
                     (select-keys article [:article/uri
                                           :article/source])
                     (when (:relations? db)
                       (map (fn [rel]
                              {:article/uri (:target/uri rel)
                               :article/source (:target/source rel)})
                            (:article/relations article))))
                ids (map #(hash-map :source (:article/source %) :uri (:article/uri %)) ids)]
            (ids->blunt ids))
          selection?
          (ids->blunt (:selected-articles db))
          :else
          (normalized-blunt-new (:search-query db)))
        mime-type (if (= (:format db) :docx)
                    docx-mime
                    "text/plain")
        format (cond (= (:format db) :docx)
                     (keyword
                      (str (name (:format db)) "-" (name (:citation-format db))
                           (when (:abstracts? db)
                             "-abstract")))
                     (= (:format db) :bib)
                     :bibtex
                     :else
                     (:format db))
        [order order-direction] (if (:single-article? db)
                                  [nil nil]
                                  (str/split (name (:query-order db)) "-"))]
    {:db (assoc db :loading? true)
     :fx [[::network/request [{:endpoint :app/save-settings}
                              {:body {:settings {:articles-export (-> {}
                                                                      (assoc :format (get db :format))
                                                                      (assoc :citation-format (get db :citation-format))
                                                                      (assoc :relations? (get db :relations?))
                                                                      (assoc :abstracts? (get db :abstracts?))
                                                                      (assoc :exclude-docsearch-link? (get db :exclude-docsearch-link?)))}}}]]
          [::network/request [{:endpoint :search/export}
                              (cond-> {:body (if (:single-article? db)
                                               (stringify-keys-with-namespaces {:limit 1000
                                                                                :blunt blunt
                                                                                :citation-format (:citation-format db)
                                                                                :format (name format)})
                                               (stringify-keys-with-namespaces (cond-> {:blunt blunt
                                                                                        :format (name format)
                                                                                        :order order
                                                                                        :citation-format (:citation-format db)
                                                                                        :order-direction order-direction
                                                                                        :relations? (:relations? db)
                                                                                        :exclude-docsearch-link? (:exclude-docsearch-link? db)}
                                                                                 (not selection?)
                                                                                 (assoc :limit
                                                                                        (min max-limit (js/parseInt (:limit db)))))))

                                       :on-success [::success-export {:filename (:filename db)
                                                                      :mime-type mime-type
                                                                      :format (:format db)}]
                                       :on-failure [::failure-export]}
                                (= (:format db) :docx)
                                (assoc :response-content-type :array-buffer
                                       :response-content-types
                                       {"text/plain"               :array-buffer
                                        "application/octet-stream" :array-buffer}))]]]})))

(rf/reg-event-fx
 ::success-export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [filename mime-type ::fetch-fx/response]}]]
  {:db (assoc db :loading? false)
   :fx [[::dre.fx/download {:content (:body response)
                            :filename filename
                            :mimetype mime-type
                            :utf8 (= (:format db) :csv)}]
        [:dispatch [::modals/clear]]]}))

(rf/reg-event-fx
 ::failure-export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (assoc db :loading? false)
   :dispatch-n [[:dre.network/default-failure opts]]}))

;; subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))
